import React from 'react';
import { formatDublinDateTime } from '../../utils/date';
import { Link } from 'react-router-dom';
import { SparklesIcon } from '@heroicons/react/24/outline';
import { useListStudentObservationsQuery } from '../../store/api/localApi';
import { ObservationStatus, ObservationVisibility } from '../../types';

interface Props {
  studentId: string;
  /** Resolved by the caller (e.g. urlPrefix from useLocationContext) so the
   *  observation-detail links route under the viewer's correct scope. */
  detailHrefBase: string;
}

function formatDateTime(iso: string): string {
  return formatDublinDateTime(iso);
}

/**
 * Admin audit view of every observation about one student across every
 * session. STAFF_ONLY rows render with a chip; WITHDRAWN rows render
 * muted with a footer of who withdrew, when. Parents never reach this
 * surface — the page that hosts the section is staff/admin only.
 */
const ObservationsAuditSection: React.FC<Props> = ({ studentId, detailHrefBase }) => {
  const { data, isLoading, error } = useListStudentObservationsQuery({ studentId, limit: 100 });
  const observations = data?.observations ?? [];

  return (
    <div className="mt-8">
      <h3 className="text-lg leading-6 font-medium text-gray-900 flex items-center gap-2">
        <SparklesIcon className="h-5 w-5 text-gray-400" />
        Observations
      </h3>
      <div
        data-testid="student-detail-observations-list"
        className="mt-3 bg-white rounded-lg shadow divide-y divide-gray-100 overflow-hidden"
      >
        {isLoading && <p className="px-4 py-6 text-sm text-gray-500 text-center">Loading…</p>}
        {error && !isLoading && (
          <p className="px-4 py-6 text-sm text-red-500 text-center">Failed to load observations.</p>
        )}
        {!isLoading && !error && observations.length === 0 && (
          <p className="px-4 py-6 text-sm text-gray-500 text-center">
            No observations recorded yet.
          </p>
        )}
        {observations.map((o) => {
          const isWithdrawn = o.status === ObservationStatus.WITHDRAWN;
          return (
            <Link
              key={o.id}
              to={`${detailHrefBase}/observations/${o.id}`}
              data-testid={`student-detail-observation-${o.id}`}
              className={`block px-4 py-3 hover:bg-gray-50 ${
                isWithdrawn ? 'opacity-60 italic' : ''
              }`}
            >
              <div className="flex justify-between items-baseline gap-2">
                <span className="font-medium text-sm text-gray-900">{o.authorName}</span>
                <span className="text-xs text-gray-400 flex-shrink-0">
                  {formatDateTime(o.createdAt)}
                </span>
              </div>
              <p className="text-sm text-gray-700 mt-1 line-clamp-2">{o.text}</p>
              <div className="flex flex-wrap gap-2 mt-1">
                {o.visibility === ObservationVisibility.STAFF_ONLY && (
                  <span className="text-xs font-medium text-amber-700 bg-amber-100 px-1.5 py-0.5 rounded">
                    Staff only
                  </span>
                )}
                {o.photos.length > 0 && (
                  <span className="text-xs text-gray-500">📸 {o.photos.length}</span>
                )}
                {isWithdrawn && (
                  <span className="text-xs text-gray-500">
                    Withdrawn{o.withdrawnAt ? ` ${formatDateTime(o.withdrawnAt)}` : ''}
                  </span>
                )}
              </div>
            </Link>
          );
        })}
      </div>
    </div>
  );
};

export default ObservationsAuditSection;
